/** DSR erasure confirmation modal — Phase 2B-ui.
 *
 * Erasure is irreversible. The modal requires the admin to type the
 * target's email to confirm (defence against the "muscle-memory click
 * the wrong row" failure mode that wipes the wrong user's PII). */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { eraseUser } from "./api";
import type { DsrUserEraseSummary, OrgMember } from "./types";


type Props = {
  member: OrgMember;
  onClose: () => void;
};


export function DsrEraseModal({ member, onClose }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [typed, setTyped] = useState("");
  const [result, setResult] = useState<DsrUserEraseSummary | null>(null);

  const erase = useMutation({
    mutationFn: () => eraseUser(member.user_id),
    onSuccess: (summary) => {
      setResult(summary);
      void qc.invalidateQueries({ queryKey: ["org-members"] });
    },
  });

  const confirmed = typed.trim() === member.email;

  if (result) {
    const preservedFields = Object.entries(result.preserved)
      .filter(([, v]) => v)
      .map(([k]) => k.replace("_", " "))
      .join(", ");
    return (
      <ModalShell onClose={onClose} title={t("ui.dsrEraseModal.completeTitle")}>
        <p className="text-sm text-nb-text">
          {result.already_erased
            ? t("ui.dsrEraseModal.alreadyErased")
            : t("ui.dsrEraseModal.redacted", {
                fields: result.redacted_fields.join(", "),
              })}
        </p>
        <p className="text-xs text-nb-text-3">
          {t("ui.dsrEraseModal.preserved", {
            basis: result.preserved_legal_basis,
            fields: preservedFields,
          })}
        </p>
        <div className="flex justify-end pt-2">
          <button type="button" className="btn-primary text-sm" onClick={onClose}>
            {t("ui.dsrEraseModal.close")}
          </button>
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell onClose={onClose} title={t("ui.dsrEraseModal.eraseTitle")}>
      <div className="space-y-2 text-sm text-nb-text">
        <p>
          <Trans i18nKey="ui.dsrEraseModal.tombstoneBody" values={{ email: member.email }}>
            This tombstones <strong>{"{{email}}"}</strong>&apos;s identifiers:
            email, names, and login become unusable. The User row stays
            (for FK integrity) but PII is redacted.
          </Trans>
        </p>
        <p className="text-xs text-nb-text-2">
          <Trans i18nKey="ui.dsrEraseModal.preservedNote">
            Audit events, annotations, evidence, sessions, and chat content
            are <strong>preserved</strong> under GDPR Art. 17(3)(e)
            (eIDAS evidence-grade carve-out). This action is{" "}
            <strong className="text-cvp-warn">irreversible</strong>.
          </Trans>
        </p>
      </div>
      <label className="block space-y-1 text-sm">
        <span className="text-nb-text-2">
          {t("ui.dsrEraseModal.confirmPrompt")}
        </span>
        <input
          className="input w-full"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={member.email}
          aria-label={t("ui.dsrEraseModal.confirmEmailAria")}
        />
      </label>
      {erase.isError && (
        <p className="text-xs text-cvp-danger">
          {(erase.error as Error).message ?? t("ui.dsrEraseModal.erasureFailed")}
        </p>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" className="btn-ghost text-sm" onClick={onClose}>
          {t("ui.dsrEraseModal.cancel")}
        </button>
        <button
          type="button"
          className="btn-danger text-sm"
          onClick={() => erase.mutate()}
          disabled={!confirmed || erase.isPending}
        >
          {erase.isPending ? t("ui.dsrEraseModal.erasing") : t("ui.dsrEraseModal.erase")}
        </button>
      </div>
    </ModalShell>
  );
}


function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div
      className="modal-backdrop bg-black/60"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-md space-y-3 rounded-lg border border-nb-border bg-nb-bg p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-baseline justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-nb-text">
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-nb-text-3 hover:text-nb-text"
            aria-label={t("ui.dsrEraseModal.closeAria")}
          >
            ×
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}
